<script setup>
import { ref } from 'vue'
import AppBtnFileUploadInput from "@/components/input/AppBtnFileUploadInput.vue"

const emit = defineEmits(['update:model-value'])
const dialog = inject('camera-translate')
const videoRef = ref(null)
const canvasRef = ref(null)
const photoUrl = ref(null)
const photoFile = ref(null)
const photoTaken = ref(false)
const text = ref(null)
let stream = null

const handleSave = () => {
  dialog.show = false
  dialog.content = text.value

}

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
const facingMode = isMobile() ? "environment" : "user"

const startCamera = async () => {
  photoUrl.value = null
  photoFile.value = null
  photoTaken.value = null
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: facingMode || 'environment' },
        width: { ideal: 3000 },   // thử 2560, 3000, 4000 tùy thiết bị
        height: { ideal: 2250 },   // tỉ lệ 4:3
        frameRate: { ideal: 30 },
      },
    })
    if (videoRef.value) videoRef.value.srcObject = stream
  } catch (error) {
    console.error('Error accessing camera:', error)
  }
}

const stopCamera = () => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
    stream = null
  }
}

onUnmounted(() => {
  stopCamera()
})

const takePhoto = async () => {
  if (!stream) {
    await startCamera()
  }
  const video = videoRef.value
  const canvas = canvasRef.value
  if (!video || !canvas) return

  canvas.width = video.videoWidth
  canvas.height = video.videoHeight

  const context = canvas.getContext('2d')

  context.drawImage(video, 0, 0)

  canvas.toBlob(blob => {
    if (blob) {
      photoUrl.value = URL.createObjectURL(blob)
      photoFile.value = new File([blob], 'photo.jpg', { type: blob.type })
      photoTaken.value = true
      stopCamera()
    }
  }, 'image/jpeg')
}

const onUpload = files => {
  const file = Array.isArray(files) ? files[0] : files
  if (file) {
    photoFile.value = file
    photoTaken.value = true
    processFile(file)
  }
}

function processFile(file) {
  const reader = new FileReader()

  reader.onload = e => {
    photoUrl.value = e.target.result
  }
  reader.readAsDataURL(file)
}

const close = () => {
  dialog.value = false
  stopCamera()
  photoUrl.value = null
  photoFile.value = null
  photoTaken.value = false
}

watch(() => dialog.show, val => {
  if (val) startCamera()
  else stopCamera()
})

watch(() => photoFile.value, file => {
  handleDecodeText(file)
})

const loading = reactive({
  status: false,
})

const handleDecodeText = async file => {
  const formData = new FormData()

  formData.append("image", file)
  loading.status = true
  text.value = null

  const { data } = await useApiRequest('image_to_text', {
    method: "POST",
    body: formData,
    isFetch: true,
  }).catch(e => {
    console.log('handleSubmit', e)
  }).finally(() => {
    loading.status = false
  })

  if (data?.value?.data) {
    text.value = `${data.value?.data}`.replace(/```/g, "")
  }
}
</script>

<template>
  <VDialog
    v-model="dialog.show"
    width="800"
  >
    <DialogCloseBtn @click="dialog.show = false" />
    <VCard class="">
      <div class="overflow-hidden">
        <video
          v-show="!photoTaken"
          ref="videoRef"
          autoplay
          playsinline
          width="100%"
        />
        <canvas
          ref="canvasRef"
          class="d-none"
        />
        <VImg
          v-show="photoUrl"
          :src="photoUrl"
        />
      </div>
      <VCardText>
        <div class="d-f-r gap-4 mb-4">
          <VBtn
            prepend-icon="tabler-camera"
            class="d-f-1"
            variant="tonal"
            @click="takePhoto"
          >
            Chụp ảnh
          </VBtn>
          <VBtn
            v-if="photoFile"
            prepend-icon="tabler-camera-rotate"
            color="secondary"
            variant="tonal"
            @click="startCamera"
          >
            Chụp
            lại
          </VBtn>
          <AppBtnFileUploadInput
            @click="stopCamera"
            @upload="onUpload"
          />
        </div>

        <div>Kết quả</div>
        <div style="position: relative">
          <AppTextarea
            v-model="text"
            auto-grow
            style="outline: none"
          />
          <div
            v-if="loading.status"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;flex-direction: column; display: flex; align-items: center; justify-content: center"
          >
            <div class="mb-2">
              Đang xử lý hình ảnh...
            </div>
            <VProgressLinear
              indeterminate
              style="width: 120px"
              color="primary"
              stream
              height="2px"
              stroke-width="1px"
              striped
            />
          </div>
        </div>
        <VBtn
          variant="tonal"
          class="w-100 mt-4"
          @click="handleSave"
        >
          Nhập liệu
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
</template>
